import React, { useMemo } from 'react';
import {
  ContentfulArticle,
  ContentfulCompetition,
  ContentfulEntry,
  ContentfulGenericPage,
  EntryType,
} from '@jbhi-fi/apis/src/contentful/types';
import { CardProps } from '../../Card';
import { CardGroup, CardGroupProps, MAX_CARDS_PER_ROW } from '../../CardGroup/CardGroup';
import { useCardEntries } from '../../hooks/useCardEntries';
import { sortEntriesByPublishedOrCreatedDateDesc } from '../../hooks/useCards';
import { ContentfulFilter } from '../../hooks/useContentfulEntries/useContentfulEntries';
import { CardTransformer } from '../transformers/CardTransformer';

type ContentfulCardEntry = ContentfulArticle | ContentfulCompetition | ContentfulGenericPage;

export type CardGroupRendererProps = CardGroupProps & {
  contentfulFilters?: Array<ContentfulFilter>;
  hideHeading?: boolean;
};

const DEFAULT_DESKTOP_ROWS = 1;

export const getCardGroupFetchCount = (desktopRows = DEFAULT_DESKTOP_ROWS) =>
  Math.max(desktopRows, 0) * MAX_CARDS_PER_ROW;

const isCardCompatibleEntry = (entry: ContentfulEntry<unknown>): entry is ContentfulCardEntry => {
  const contentType = entry?.sys?.contentType?.sys?.id;
  return (
    contentType === EntryType.article || contentType === EntryType.competition || contentType === EntryType.genericPage
  );
};

export const mergeCards = ({
  explicitCards,
  fetchedCards,
}: {
  explicitCards: Array<CardProps>;
  fetchedCards: Array<CardProps>;
}): Array<CardProps> => {
  const seen = new Set<string>();
  return explicitCards.concat(fetchedCards).filter(card => {
    const key = card.href || card.heading;
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const CardGroupRenderer: React.FC<CardGroupRendererProps> = ({
  cards: explicitCards,
  heading,
  linkProps,
  contentfulFilters = [],
  desktopRows = DEFAULT_DESKTOP_ROWS,
  hideHeading = false,
  ...props
}) => {
  const displayCount = getCardGroupFetchCount(desktopRows);
  const explicitDisplayCards = useMemo(
    () => mergeCards({ explicitCards, fetchedCards: [] }).slice(0, displayCount),
    [displayCount, explicitCards]
  );
  const shouldFetchCards = contentfulFilters.length > 0 && explicitDisplayCards.length < displayCount;
  const { entries: fetchedEntries } = useCardEntries({
    filters: contentfulFilters,
    page: 1,
    // Fetch a full set of candidates so duplicates against explicit cards can still backfill the display count.
    pageSize: shouldFetchCards ? displayCount : 0,
    appendResults: false,
    enabled: shouldFetchCards,
    logPrefix: 'CardGroupRenderer',
  });
  const fetchedCards = useMemo(
    () =>
      fetchedEntries
        .filter(isCardCompatibleEntry)
        .sort(sortEntriesByPublishedOrCreatedDateDesc)
        .map(CardTransformer.toCardProps) as Array<CardProps>,
    [fetchedEntries]
  );
  const cards = useMemo(
    () => mergeCards({ explicitCards: explicitDisplayCards, fetchedCards }).slice(0, displayCount),
    [displayCount, explicitDisplayCards, fetchedCards]
  );

  return (
    <CardGroup
      {...props}
      cards={cards}
      desktopRows={desktopRows}
      heading={hideHeading ? '' : heading}
      linkProps={hideHeading ? undefined : linkProps}
    />
  );
};
